import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";
import type { ProfileStatus } from "./profile-gate";
import { db } from "../firebase";

export interface ContactProfile {
  company: string | null;
  phone: string | null;
}

/**
 * The company and phone the user saved on /complete-profile, read from their
 * own `users/{uid}` doc (the rules let a user read it). A doc that doesn't
 * exist yet — the signup trigger may not have run — reads as an empty profile.
 */
export async function readContactProfile(uid: string): Promise<ContactProfile> {
  const snap = await getDoc(doc(db, "users", uid));
  return toProfile(snap.data());
}

function toProfile(data: Record<string, unknown> | undefined): ContactProfile {
  return {
    company: typeof data?.company === "string" && data.company ? data.company : null,
    phone: typeof data?.phone === "string" && data.phone ? data.phone : null
  };
}

/**
 * Whether the signed-in user has filled in company and phone, live — so the
 * moment /complete-profile saves, every page knows. A listener that fails
 * counts as complete: not knowing must not lock anyone out.
 */
export function useProfileStatus(uid: string | null): ProfileStatus {
  const [state, setState] = useState<{ uid: string | null; status: ProfileStatus }>({ uid, status: "loading" });
  useEffect(() => {
    if (!uid) return;
    return onSnapshot(
      doc(db, "users", uid),
      (snap) => setState({ uid, status: isProfileComplete(toProfile(snap.data())) ? "complete" : "incomplete" }),
      (e) => {
        console.warn("[profile] listener failed, not gating", e);
        setState({ uid, status: "complete" });
      }
    );
  }, [uid]);
  if (!uid) return "complete";
  // A result for a previous account (sign-out, sign-in as someone else) is stale.
  return state.uid === uid ? state.status : "loading";
}

export function isProfileComplete(p: ContactProfile): boolean {
  return !!p.company && !!p.phone;
}
